import { useEffect, useRef, useState } from "react";
import { Workspace } from "../components/Shell";
import { SettingsHead } from "../components/AccountTabs";
import { ReminderRow } from "../components/Reminders";
import { ReminderWindow, type ReminderTarget } from "../components/ReminderWindow";
import { coachZone } from "../lib/days";
import { REMINDERS_PAGE_EMPTY, doneFoldLabel, groupReminders } from "../lib/reminders";
import { useReminders } from "./useReminders";
import { useOpenerFocus } from "../components/useOpenerFocus";

/* W82 STEP 6 — THE REMINDERS PAGE, under the Account: every reminder the coach wrote, grouped by the day it falls on
 * in the coach's own clock (`groupReminders`), the done ones folded under the list. `+ Reminder` is the page's ONE
 * primary; a row's title opens the same window to edit or delete it; the round tick marks it done. */
export function RemindersPage({ coachId, foreground = true }: { coachId: string; foreground?: boolean }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);
  // ONE instant and ONE zone per load, so no two groups can disagree about which day is today. An INSTANT, never a day:
  // every day here is `localDayKey(now, zone)` in the coach's zone (the Dashboard's own `instant` idiom).
  const [instant] = useState(() => Date.now());
  const [now] = useState(() => new Date(instant));
  const [zone] = useState(() => coachZone());
  const { state, write } = useReminders(coachId, zone);
  const [target, setTarget] = useState<ReminderTarget | null>(null);
  /* JOB 3 STEP 5 PAGE 6 — the window is mounted ON DEMAND (it sat in the page as a closed dialog with its four fields);
     focus goes back to the control that opened it — `+ Reminder` or the row's title — or the heading when that row is gone. */
  const remember = useOpenerFocus(target !== null, heading);
  const open = (t: ReminderTarget) => { remember(); setTarget(t); };
  const [showDone, setShowDone] = useState(false);
  const g = state.status === "ready" ? groupReminders(state.rows, now, zone) : null;
  const clients = state.status === "ready" ? state.clients : [];
  const fold = g === null ? null : doneFoldLabel(g.done.length, showDone);

  return (
    <Workspace>
      <main id="main" className="account-page reminders-page ours-page">
        <SettingsHead current="reminders" headingRef={heading} action={
          <button type="button" className="ht-button ht-button-primary" onClick={() => open({ mode: "new", clientId: null })}>
            + Reminder
          </button>
        } />

        {state.status === "loading" && <p className="ht-callout ours-quiet" aria-live="polite">Loading your reminders…</p>}
        {state.status === "error" && (
          <div role="alert" className="ours-error">
            <p className="ht-body">Could not load your reminders.</p>
            <p className="ht-caption">{state.message}</p>
          </div>
        )}
        {g !== null && g.open.length === 0 && g.done.length === 0 && <p className="ht-callout ours-quiet">{REMINDERS_PAGE_EMPTY}</p>}

        {g !== null && g.open.map((group) => (
          <section key={group.key} className="queue-card reminders-day" aria-labelledby={`reminders-${group.key}`}>
            <header className="queue-card-head">
              <h2 id={`reminders-${group.key}`} className="queue-card-title">{group.label}</h2>
              <span className="queue-card-sub">{group.rows.length}</span>
            </header>
            <ul className="reminder-rows">
              {group.rows.map((r) => (
                <ReminderRow key={r.id} r={r} now={now} zone={zone} clients={clients} write={write} onEdit={open} timeOnly />
              ))}
            </ul>
          </section>
        ))}

        {g !== null && fold !== null && (
          <section className="reminders-done" aria-label="Done reminders">
            <button type="button" className="ht-button ht-button-quiet reminders-fold" aria-expanded={showDone}
                    onClick={() => setShowDone((s) => !s)}>{fold}</button>
            {showDone && (
              <div className="queue-card">
                <ul className="reminder-rows">
                  {g.done.map((r) => (
                    <ReminderRow key={r.id} r={r} now={now} zone={zone} clients={clients} write={write} onEdit={open} />
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {target !== null && (
          <ReminderWindow target={target} onClose={() => setTarget(null)} clients={clients} write={write} zone={zone} now={now} />
        )}
      </main>
    </Workspace>
  );
}
